import { useLingui } from '@lingui/react/macro'
import { useNavigate } from '@tanstack/react-router'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import {
  MD_CASE_SEVERITIES,
  type MdCaseSeverity,
} from 'features/mdCases/mdCase'
import { useGeckoNotifications } from 'features/notifications'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import { useScatterplotContext } from 'features/timeSeries'
import React, { useCallback, useMemo, useState } from 'react'
import { SEARCH_PARAM_SITE_ID } from 'routes/(global_search_params)/-searchParams'
import { SEARCH_PARAM_CASE_ID } from 'routes/cases/-searchParams'
import { GeckoAlert, RolodexEntryType } from 'types'

import { MdDraftAlertCaseFlowPanels } from './MdDraftAlertCaseFlowPanels'
import type {
  MdDraftAlertCaseFlowProps,
  OverlayAlert,
  PositionedOverlayAlert,
} from './mdDraftAlertCaseFlowTypes'
import {
  buildCaseAlertRemovalPatch,
  parseManualAlerts,
  selectManualAlertsForSensors,
} from './mdDraftAlertCaseFlowUtils'
import { useManualAlertOverlay } from './useManualAlertOverlay'

const EMPTY_POSITIONED_ALERTS: readonly PositionedOverlayAlert[] = []

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value)

const mergeAlertsById = (
  ...alertGroups: readonly GeckoAlert[][]
): GeckoAlert[] => [
  ...new Map(
    alertGroups.flatMap((alerts) => alerts.map((alert) => [alert.id, alert]))
  ).values(),
]

const toOverlayAlert = (alert: GeckoAlert): OverlayAlert => ({
  id: alert.id,
  title: alert.data.title ?? null,
  summary: alert.data.summary ?? '',
  severity: MD_CASE_SEVERITIES.includes(alert.data.severity as MdCaseSeverity)
    ? (alert.data.severity as MdCaseSeverity)
    : null,
  primarySensorId:
    alert.data.primary_datapoint_id ?? alert.data.datapoint_ids?.[0] ?? '',
  sensorIds: alert.data.datapoint_ids ?? [],
  renderStartAt: alert.data.render_start_at ?? '',
  renderEndAt: alert.data.render_end_at ?? '',
  focusStartAt: alert.data.focus_start_at ?? '',
  focusEndAt: alert.data.focus_end_at ?? '',
  caseId: alert.data.case_id ?? null,
})

export const MdDraftAlertCaseFlow: React.FC<MdDraftAlertCaseFlowProps> = ({
  uPlotRef,
  chartAreaRef,
  initialManualAlertId,
  allowSavedAlertDelete = true,
}) => {
  const { t } = useLingui()

  const navigate = useNavigate()
  const { rolodexPatch, rolodexDelete } = useRolodexEdit()
  const { errorNotification } = useGeckoNotifications()
  const {
    siteID,
    selectedSensors,
    chartData,
    setHighlightedSensorId,
    setSecondaryHighlightedSensorIds,
    xAxisSensorId,
    isLoading,
    isInitializing,
    hideAllAlerts,
  } = useScatterplotContext()
  const { data: savedAlertEntries = [], mutate: refreshSavedAlerts } =
    useGetRolodexEntriesByLoadOptions<unknown[]>(
      siteID
        ? {
            type: [RolodexEntryType.ALERT],
            tags: { site: [siteID] },
            limit: 1000,
          }
        : {
            type: [],
            limit: 0,
          }
    )
  const { data: selectedAlertEntries = [] } = useGetRolodexEntriesByLoadOptions<
    unknown[]
  >(
    initialManualAlertId
      ? {
          ids: [initialManualAlertId],
          type: [RolodexEntryType.ALERT],
          limit: 1,
        }
      : {
          type: [],
          limit: 0,
        }
  )
  const savedManualAlerts = useMemo(
    () =>
      mergeAlertsById(
        parseManualAlerts(savedAlertEntries),
        parseManualAlerts(selectedAlertEntries)
      ),
    [savedAlertEntries, selectedAlertEntries]
  )

  const [isDeletingAlertId, setIsDeletingAlertId] = useState<string | null>(
    null
  )
  const [dismissedAlertIds, setDismissedAlertIds] = useState<Set<string>>(
    () => new Set()
  )

  const sensorsById = useMemo(
    () => new Map(selectedSensors.map((sensor) => [sensor.id, sensor])),
    [selectedSensors]
  )
  const selectedSensorIds = useMemo(
    () => selectedSensors.map((sensor) => sensor.id),
    [selectedSensors]
  )

  const chartDataExtentKey = useMemo(() => {
    let min = Number.POSITIVE_INFINITY
    let max = Number.NEGATIVE_INFINITY
    let count = 0

    chartData.forEach((point) => {
      const timestamp = point.timestamp
      if (typeof timestamp !== 'number' || Number.isNaN(timestamp)) {
        return
      }

      count += 1
      min = Math.min(min, timestamp)
      max = Math.max(max, timestamp)
    })

    return count > 0 ? `${count}:${min}:${max}` : 'empty'
  }, [chartData])

  const overlayAlerts = useMemo(
    () =>
      selectManualAlertsForSensors({
        alerts: savedManualAlerts,
        sensorIDs: selectedSensorIds,
      }).map(toOverlayAlert),
    [savedManualAlerts, selectedSensorIds]
  )

  const linkedCaseIds = useMemo(
    () => [
      ...new Set(
        savedManualAlerts.flatMap((alert) =>
          alert.data.case_id ? [alert.data.case_id] : []
        )
      ),
    ],
    [savedManualAlerts]
  )

  const { data: linkedCasesData = [] } = useGetRolodexEntriesByLoadOptions<
    unknown[]
  >(
    linkedCaseIds.length > 0
      ? {
          ids: linkedCaseIds,
          type: [RolodexEntryType.CASE],
          limit: linkedCaseIds.length,
        }
      : {
          type: [],
          limit: 0,
        }
  )

  const linkedCasesById = useMemo(
    () =>
      new Map<string, Record<string, unknown>>(
        linkedCasesData.flatMap((entry) =>
          isRecord(entry) && typeof entry.id === 'string'
            ? [[entry.id, entry]]
            : []
        )
      ),
    [linkedCasesData]
  )

  const {
    positionedAlerts,
    activePositionedAlert,
    startHoverAlert,
    scheduleHoverAlertHide,
    clearHoverAlert,
  } = useManualAlertOverlay({
    overlayAlerts,
    uPlotRef,
    chartAreaRef,
    xAxisSensorId,
    initialManualAlertId,
    chartDataLength: chartData.length,
    chartDataExtentKey,
    selectedSensorsLength: selectedSensors.length,
    isChartLoading: isLoading || isInitializing,
    setHighlightedSensorId,
    setSecondaryHighlightedSensorIds,
  })

  const handleToggleAlert = useCallback(
    (alertId: string) => {
      clearHoverAlert()
      setDismissedAlertIds((current) => {
        const next = new Set(current)
        if (next.has(alertId)) {
          next.delete(alertId)
        } else {
          next.add(alertId)
        }
        return next
      })
    },
    [clearHoverAlert]
  )

  const handleOpenCase = (caseId: string | null) => {
    if (!caseId) {
      return
    }

    navigate({
      to: '/cases/details',
      search: {
        [SEARCH_PARAM_SITE_ID]: siteID,
        [SEARCH_PARAM_CASE_ID]: caseId,
      },
    })
  }

  const handleDeleteAlert = async (alert: OverlayAlert) => {
    if (isDeletingAlertId === alert.id) {
      return
    }

    const confirmationMessage = alert.caseId
      ? t`Delete this alert? It will also be removed from the linked M&D case.`
      : t`Delete this alert?`

    if (!window.confirm(confirmationMessage)) {
      return
    }

    setIsDeletingAlertId(alert.id)

    try {
      const casePatch =
        alert.caseId && linkedCasesById.has(alert.caseId)
          ? buildCaseAlertRemovalPatch(
              linkedCasesById.get(alert.caseId)!,
              alert.id
            )
          : null

      if (casePatch) {
        await rolodexPatch([casePatch])
      }

      await rolodexDelete(alert.id, false)
      clearHoverAlert()
      await refreshSavedAlerts()
    } catch (error) {
      errorNotification(
        t`Failed to delete alert: ${{ value281_53: String(error) }}`
      )
    } finally {
      setIsDeletingAlertId((current) => (current === alert.id ? null : current))
    }
  }

  return (
    <MdDraftAlertCaseFlowPanels
      chartAreaRef={chartAreaRef}
      positionedAlerts={
        hideAllAlerts ? EMPTY_POSITIONED_ALERTS : positionedAlerts
      }
      activePositionedAlert={hideAllAlerts ? null : activePositionedAlert}
      sensorsById={sensorsById}
      isDeletingAlertId={isDeletingAlertId}
      allowSavedAlertDelete={allowSavedAlertDelete}
      onDeleteAlert={handleDeleteAlert}
      onOpenCase={handleOpenCase}
      onStartHoverAlert={startHoverAlert}
      onScheduleHoverAlertHide={scheduleHoverAlertHide}
      onToggleAlert={handleToggleAlert}
      dismissedAlertIds={dismissedAlertIds}
    />
  )
}
